"use client";

import { IconCheck, IconDeviceFloppy } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useTrackSegmentInViewport } from "@/hooks/useTrackSegmentInViewport";
import { trackItemSavedForLater, trackSaveItemClicked } from "@/lib/analytics/segment";
import { buildSaveForLaterRequest } from "@/lib/cart";
import { useSaveForLater } from "@/mutations/useSaveForLater";
import { useProduct } from "@/queries/useProduct";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { usePdpStore } from "@/stores/usePdpStore";
import { useProductBuilderContext } from "./ProductBuilderContext";
import SaveForLaterEmailDialog from "./SaveForLaterEmailDialog";
interface ButtonLabelParams {
  isPending: boolean;
  isSuccess: boolean;
  lastSaveWasUpdate: boolean;
  hasSavedItem: boolean;
}
function getButtonLabel({
  isPending,
  isSuccess,
  lastSaveWasUpdate,
  hasSavedItem
}: ButtonLabelParams) {
  if (isPending && lastSaveWasUpdate) return "Updating...";
  if (isPending) return "Saving...";
  if (isSuccess && lastSaveWasUpdate) return "Updated";
  if (isSuccess) return "Saved";
  if (hasSavedItem) return "Update Saved Item";
  return "Save Progress";
}
export default function SaveForLaterButton() {
  const {
    product
  } = useProductBuilderContext();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const customerId = useAuthClaimsStore(state => state.claims?.sg_customer_id);
  const mutation = useSaveForLater();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [savedSpecId, setSavedSpecId] = useState<string>();
  const [lastSaveWasUpdate, setLastSaveWasUpdate] = useState(false);
  const guestEmailRef = useRef<string>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const configKey = usePdpStore(state_0 => [state_0.productSku, state_0.material, state_0.shape, state_0.laminate, state_0.finish, state_0.adhesive, state_0.orientation, state_0.quantity, state_0.width, state_0.height, state_0.specialInstructions, state_0.uploadedFileInfo?.filepath].join("|"));
  const savedConfigRef = useRef<string | undefined>(undefined);
  const resetMutation = mutation.reset;
  const shouldReset = mutation.isSuccess && !!savedConfigRef.current && configKey !== savedConfigRef.current;
  useLayoutEffect(() => {
    if (!shouldReset) return;
    resetMutation();
  }, [shouldReset, resetMutation]);
  useTrackSegmentInViewport(buttonRef, "Save Item In Viewport", {
    threshold: 0.5
  });
  const hasSavedItem = !!savedSpecId;
  function saveAndTrack(request: Parameters<typeof mutation.mutate>[0]) {
    setLastSaveWasUpdate(hasSavedItem);
    trackSaveItemClicked(!!customerId, request);
    mutation.mutate(request, {
      onSuccess: response => {
        if (!response) return;
        trackItemSavedForLater({
          request,
          response,
          isEditing: hasSavedItem
        });
        setSavedSpecId(response.savedSpec.id);
        savedConfigRef.current = configKey;
      }
    });
  }
  const handleSave = () => {
    if (!productAdmin) return;
    const email = customerId ? undefined : guestEmailRef.current;
    if (!customerId && !email) {
      setDialogOpen(true);
      return;
    }
    saveAndTrack(buildSaveForLaterRequest({
      productAdmin,
      email,
      specId: savedSpecId
    }));
  };
  const handleEmailSave = (email_0: string) => {
    if (!productAdmin) return;
    guestEmailRef.current = email_0;
    saveAndTrack(buildSaveForLaterRequest({
      productAdmin,
      email: email_0,
      specId: savedSpecId
    }));
  };
  const buttonLabel = getButtonLabel({
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    lastSaveWasUpdate,
    hasSavedItem
  });
  return <div className="flex flex-1 flex-col gap-3">
      <Button ref={buttonRef} size="xl" variant="outline" className="w-full" onClick={handleSave} disabled={mutation.isPending || mutation.isSuccess}>
        {mutation.isSuccess ? <IconCheck /> : <IconDeviceFloppy />}
        {buttonLabel}
      </Button>
      {mutation.isSuccess && <output className="flex items-center gap-1 text-sm font-medium text-green-600">
          <IconCheck size={16} />
          {lastSaveWasUpdate ? "Item progress updated." : "Saved — check your email for a link."}
        </output>}
      {mutation.isError && <p role="alert" className="text-sm font-medium text-red-600">
          Failed to save. Please try again.
        </p>}
      <SaveForLaterEmailDialog open={dialogOpen} onOpenChange={setDialogOpen} onSave={handleEmailSave} isPending={mutation.isPending} isSuccess={mutation.isSuccess} isError={mutation.isError} />
    </div>;
}